import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chatApi, ChatStreamError } from '../services/api';
import { ChatStreamEvent, SendChatMessagePayload } from '../types/api';
import { TODOS_KEY } from './useTodos';
import { CATEGORIES_KEY } from './useCategories';
import { TIME_LOGS_KEY } from './useTimeLogs';

export const CHAT_THREADS_KEY = ['chat', 'threads'] as const;
export const chatThreadKey = (id: number) => ['chat', 'thread', id] as const;
export const CHAT_USAGE_KEY = ['chat', 'usage'] as const;

export const useChatThreads = () =>
  useQuery({ queryKey: CHAT_THREADS_KEY, queryFn: chatApi.listThreads });

export const useChatUsage = () => useQuery({ queryKey: CHAT_USAGE_KEY, queryFn: chatApi.getUsage });

export const useChatThread = (threadId: number | null) =>
  useQuery({
    queryKey: chatThreadKey(threadId ?? -1),
    queryFn: () => chatApi.getThread(threadId as number),
    enabled: threadId != null,
  });

export interface StreamChatMessageVariables {
  payload: SendChatMessagePayload;
  /** each token and tool call as it arrives */
  onEvent: (event: ChatStreamEvent) => void;
  signal?: AbortSignal;
}

/** Send a message and stream the reply (see chatApi.streamMessage). */
export const useSendChatMessage = () => {
  const queryClient = useQueryClient();

  const refreshAfterReply = (threadId: number | null, toolCalled: boolean) => {
    if (threadId != null) queryClient.invalidateQueries({ queryKey: chatThreadKey(threadId) });
    queryClient.invalidateQueries({ queryKey: CHAT_THREADS_KEY });
    // The agent may have created/changed tasks, categories or time logs.
    if (toolCalled) {
      queryClient.invalidateQueries({ queryKey: TODOS_KEY });
      queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY });
      queryClient.invalidateQueries({ queryKey: TIME_LOGS_KEY });
    }
  };

  return useMutation({
    mutationFn: ({ payload, onEvent, signal }: StreamChatMessageVariables) =>
      chatApi.streamMessage(payload, onEvent, signal),
    onSuccess: (result) => refreshAfterReply(result.thread_id, !!result.tool_calls?.length),
    onError: (err) => {
      // Failed mid-reply: the message (and any tool calls) are already in the thread.
      if (err instanceof ChatStreamError) refreshAfterReply(err.threadId, err.toolCalled);
    },
    // Rejected messages can cost credits too (e.g. blocked by the scope guard).
    // Not returned: a returned promise would make the mutation wait for this
    // refetch before the page's own onSuccess/onError run.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CHAT_USAGE_KEY });
    },
  });
};

export const useDeleteChatThread = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (threadId: number) => chatApi.deleteThread(threadId),
    onSuccess: (_, threadId) => {
      queryClient.removeQueries({ queryKey: chatThreadKey(threadId) });
      queryClient.invalidateQueries({ queryKey: CHAT_THREADS_KEY });
    },
  });
};
